<div class="tree-container">
    <div class="tree-search">
        <p-iconfield class="flex-1">
            <p-inputicon class="pi pi-search" />
            <input pInputText type="search" class="w-full" placeholder="Find a room or location" [ngModel]="filterText()" (ngModelChange)="filterText.set($event)" aria-label="Filter locations" />
        </p-iconfield>
        @if (canCreateRoot()) {
            <button pButton type="button" class="tree-add-btn" severity="secondary" [text]="true" [rounded]="true" (click)="openCreateRoot()" pTooltip="New top-level location" tooltipPosition="left" aria-label="New top-level location">
                <i class="pi pi-plus" aria-hidden="true"></i>
            </button>
        }
    </div>
    @if (filterText().trim() && filteredNodes().length === 0) {
        <app-empty-state icon="pi pi-search" title="No matches" [description]="'Nothing named \u201c' + filterText().trim() + '\u201d in this tree.'" [compact]="true" />
    } @else if (!filterText().trim() && treeNodes().length === 0 && namespaceId()) {
        <app-empty-state icon="fa fa-map-marker" title="No locations yet" description="Rooms appear here automatically when a system checks in. You can also add buildings and floors to organise them." [compact]="true" [actionLabel]="canCreateRoot() ? 'Add a location' : null" actionIcon="pi pi-plus" (action)="openCreateRoot()" />
    }
    <p-contextmenu [model]="contextMenuItems()" #cm [appendTo]="'body'"></p-contextmenu>
    <p-treetable [value]="filteredNodes()" selectionMode="single" [tableStyle]="{ width: '100%', tableLayout: 'fixed' }" (onNodeExpand)="onExpand($event.node)" (onNodeCollapse)="onCollapse($event.node)" (onNodeSelect)="onSelect($event.node)">
        <ng-template #body let-rowNode let-rowData="rowData">
            <tr
                class="nav"
                [class.drag-over]="isValidDropTarget(rowNode.node)"
                [class.dragging]="draggedNode()?.key === rowNode.node.key"
                [attr.id]="'tree-node-' + rowNode.node.key"
                (contextmenu)="onRightClick($event, rowNode.node, cm)"
                [attr.draggable]="canDrag(rowNode.node)"
                (dragstart)="onNativeDragStart($event, rowNode.node)"
                (dragend)="onDragEnded()"
                (dragenter)="onDragEnter(rowNode.node); $event.preventDefault()"
                (dragleave)="onNativeDragLeave($event)"
                (dragover)="onNativeDragOver($event, rowNode.node)"
                (drop)="onNativeDrop($event, rowNode.node)"
            >
                <td class="nav-item" [routerLinkActive]="'active'" [routerLink]="rowNode.node.routePath" (click)="$event.stopPropagation()">
                    <div class="node-content">
                        <p-treetabletoggler [rowNode]="rowNode" (click)="$event.stopPropagation()"></p-treetabletoggler>
                        @if (canDrag(rowNode.node)) {
                            <i class="fa fa-grip-vertical drag-handle" (mousedown)="onHandleMouseDown(rowNode.node)" (mouseup)="onHandleMouseUp()" (mouseleave)="onHandleMouseUp()"></i>
                        }
                        @if (rowNode.node.icon) {
                            <i class="fa" [ngClass]="rowNode.node.icon"></i>
                        }
                        <span class="node-title" [title]="rowNode.node.label">{{ rowNode.node.label }}</span>
                    </div>
                </td>
            </tr>
        </ng-template>
    </p-treetable>
</div>

<!-- Create Dialog -->
<p-dialog [header]="creatingParent() ? 'New Sub-location' : 'New Location'" [visible]="showCreateDialog()" (onHide)="showCreateDialog.set(false)" [modal]="true" [closable]="false" appendTo="body" [style]="{ width: '420px' }">
    <div class="dialog-body">
        <div>
            <label class="block mb-1 text-sm">Name *</label>
            <input pInputText [ngModel]="createName()" (ngModelChange)="createName.set($event)" placeholder="Location name" />
        </div>
        <div>
            <label class="block mb-1 text-sm">Code</label>
            <input pInputText [ngModel]="createCode()" (ngModelChange)="createCode.set($event)" placeholder="Short code (optional)" />
        </div>
        <div>
            <label class="block mb-1 text-sm">Type</label>
            <p-select [options]="locationTypeOptions" [ngModel]="createType()" (ngModelChange)="createType.set($event)" optionLabel="label" optionValue="value" placeholder="Select type (optional)" [appendTo]="'body'"></p-select>
        </div>
        <div class="text-xs text-status-offline" *ngIf="createError()">{{ createError() }}</div>
        <div class="text-xs text-color-secondary" *ngIf="creatingParent() as p">Parent: {{ p.label }}</div>
    </div>
    <ng-template #footer>
        <button pButton severity="secondary" (click)="cancelCreate()" [disabled]="createSubmitting()"><span class="p-button-label">Cancel</span></button>
        <button pButton [disabled]="!createName().trim() || createSubmitting()" (click)="confirmCreate()"><span class="p-button-label">Create</span>
            <span class="pi pi-spin pi-spinner mr-2" *ngIf="createSubmitting()"></span>
        </button>
    </ng-template>
</p-dialog>

<!-- Delete Dialog -->
<p-dialog header="Delete Location" [visible]="showDeleteDialog()" (onHide)="showDeleteDialog.set(false)" [modal]="true" [closable]="false" appendTo="body" [style]="{ width: '420px' }">
    <div class="flex flex-col gap-4">
        <div *ngIf="deleteTarget() as dt">
            <p>
                Are you sure you want to delete <strong>{{ dt.label }}</strong
                >? This action cannot be undone.
            </p>
            <p class="text-sm text-color-secondary" *ngIf="dt.children && dt.children.length > 0">It has {{ dt.children.length }} child location(s) which will be inherited by the parent of this location.</p>
        </div>
        <div class="text-xs text-status-offline" *ngIf="deleteError()">{{ deleteError() }}</div>
    </div>
    <ng-template #footer>
        <button pButton severity="secondary" (click)="cancelDelete()" [disabled]="deleteSubmitting()"><span class="p-button-label">Cancel</span></button>
        <button pButton severity="danger" (click)="confirmDelete()" [disabled]="deleteSubmitting()"><span class="p-button-label">Delete</span>
            <span class="pi pi-spin pi-spinner mr-2" *ngIf="deleteSubmitting()"></span>
        </button>
    </ng-template>
</p-dialog>

<!-- Rename Dialog -->
<p-dialog header="Rename Location" [visible]="showRenameDialog()" (onHide)="showRenameDialog.set(false)" [modal]="true" [closable]="false" appendTo="body" [style]="{ width: '420px' }">
    <div class="flex flex-col gap-3">
        <div *ngIf="renameTarget() as rt" class="text-xs text-color-secondary">Editing: {{ rt.label }}</div>
        <div>
            <label class="block mb-1 text-sm">Name *</label>
            <input pInputText [ngModel]="renameName()" (ngModelChange)="renameName.set($event)" placeholder="Location name" />
        </div>
        <div>
            <label class="block mb-1 text-sm">Code</label>
            <input pInputText [ngModel]="renameCode()" (ngModelChange)="renameCode.set($event)" placeholder="Short code (optional)" />
        </div>
        <div>
            <label class="block mb-1 text-sm">Type</label>
            <p-select [options]="locationTypeOptions" [ngModel]="renameType()" (ngModelChange)="renameType.set($event)" optionLabel="label" optionValue="value" placeholder="Select type (optional)" [showClear]="true" [appendTo]="'body'"> </p-select>
        </div>
        <div class="text-xs text-status-offline" *ngIf="renameError()">{{ renameError() }}</div>
    </div>
    <ng-template #footer>
        <button pButton severity="secondary" (click)="cancelRename()" [disabled]="renameSubmitting()"><span class="p-button-label">Cancel</span></button>
        <button pButton (click)="confirmRename()" [disabled]="!renameName().trim() || renameSubmitting()"><span class="p-button-label">Save</span>
            <span class="pi pi-spin pi-spinner mr-2" *ngIf="renameSubmitting()"></span>
        </button>
    </ng-template>
</p-dialog>

<!-- Move Location Dialog -->
<p-dialog header="Move Location" [visible]="showMoveLocationDialog()" (onHide)="showMoveLocationDialog.set(false)" [modal]="true" [closable]="false" appendTo="body" [style]="{ width: '40rem', maxHeight: '80vh' }">
    <div class="flex flex-col gap-3" *ngIf="moveLocationTarget() as target">
        <div class="text-sm mb-3">
            Moving: <strong>{{ target.label }}</strong>
        </div>
        <div class="flex gap-2 mb-3">
            <button pButton type="button" [outlined]="moveToRoot()" [severity]="moveToRoot() ? 'secondary' : 'primary'" (click)="moveToRoot.set(false)" [disabled]="moveLocationSubmitting()"><span class="p-button-label">{{ 'Select Parent Location' }}</span></button>
            <button
                pButton
                type="button"
                [outlined]="!moveToRoot()"
                [severity]="!moveToRoot() ? 'secondary' : 'primary'"
                (click)="moveToRoot.set(true); moveLocationNewParentId.set(null)"
                [disabled]="moveLocationSubmitting()"><span class="p-button-label">{{ 'Move to Root Level' }}</span></button>
        </div>
        <div *ngIf="moveToRoot()" class="p-3 surface-100 border-1 surface-border rounded mb-3">
            <p class="text-sm m-0 text-color-secondary">This location will be moved to the top level of the tenant with no parent.</p>
        </div>
        <div *ngIf="!moveToRoot()">
            <p class="text-xs text-color-secondary mb-2">Select a new parent location from the tree below:</p>
            <div *ngIf="target.data as locationData" class="text-xs text-color-secondary mb-2">Current parent is highlighted below</div>
            <app-location-picker
                [locations]="locations() || []"
                [namespaceId]="moveLocationTarget()?.data?.appNamespaceId || null"
                [currentLocationId]="moveLocationTarget()?.data?.parentId || null"
                [disabled]="moveLocationSubmitting()"
                [minHeight]="'400px'"
                [maxHeight]="'500px'"
                (locationSelected)="moveLocationNewParentId.set($event)"
            />
        </div>
        <div class="text-xs text-status-offline" *ngIf="moveLocationError()">{{ moveLocationError() }}</div>
    </div>
    <ng-template #footer>
        <button pButton severity="secondary" (click)="cancelMoveLocation()" [disabled]="moveLocationSubmitting()"><span class="p-button-label">Cancel</span></button>
        <button pButton (click)="confirmMoveLocation()" [disabled]="moveLocationSubmitting() || (!moveToRoot() && moveLocationNewParentId() === null)"><span class="p-button-label">Move</span>
            <span class="pi pi-spin pi-spinner mr-2" *ngIf="moveLocationSubmitting()"></span>
        </button>
    </ng-template>
</p-dialog>

<!-- Create Device Dialog -->
<p-dialog [header]="createDeviceResult() ? 'Device Registration' : 'New Device'" [visible]="showCreateDeviceDialog()" (onHide)="showCreateDeviceDialog.set(false)" [modal]="true" [closable]="false" appendTo="body" [style]="{ width: '460px' }">
    <div class="flex flex-col gap-3">
        @if (!createDeviceResult()) {
            <div>
                <label class="block mb-1 text-sm">Device Name *</label>
                <input pInputText [ngModel]="createDeviceName()" (ngModelChange)="createDeviceName.set($event)" placeholder="My Device" />
            </div>
            <div>
                <label class="block mb-1 text-sm">Location *</label>
                <p-select
                    [options]="createDeviceLocationOptions()"
                    [ngModel]="createDeviceLocationId()"
                    (ngModelChange)="createDeviceLocationId.set($event)"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select location"
                    [appendTo]="'body'"
                ></p-select>
            </div>
            <div class="text-xs text-color-secondary" *ngIf="createDeviceLocationId() as lid">
                Selected ID: <code>{{ lid }}</code>
            </div>
            <div class="text-xs text-status-offline" *ngIf="createDeviceError()">{{ createDeviceError() }}</div>
        } @else {
            <div class="p-3 surface-border flex flex-col gap-3">
                <p class="m-0 text-sm text-color-secondary">Provide this one-time registration code to the device within the validity window.</p>
                <div class="flex flex-col items-start gap-1">
                    <span class="text-xs uppercase tracking-wide text-color-secondary">Registration Code</span>
                    <div class="code-box flex items-center gap-2" style="font-size: x-large">
                        {{ createDeviceResult()?.registrationCode }}
                        <button pButton type="button" class="p-button-text p-button-lg copy-btn" (click)="copyCode(createDeviceResult()?.registrationCode)" aria-label="Copy code">
                            <span class="pi pi-copy"></span>
                        </button>
                    </div>
                </div>
                <div class="text-xs" *ngIf="createDeviceResult()?.registrationExpiresUtc as exp">
                    <strong>{{ relativeExpiry(exp) }}</strong> remaining (expires {{ exp | date: 'shortTime' }} UTC)
                </div>
                <p class="text-xs text-color-secondary m-0">After expiry the code becomes invalid and a new device must be created or the code regenerated (future feature).</p>
            </div>
        }
    </div>
    <ng-template #footer>
        @if (!createDeviceResult()) {
            <button pButton severity="secondary" (click)="cancelCreateDevice()" [disabled]="createDeviceSubmitting()"><span class="p-button-label">Cancel</span></button>
            <button pButton (click)="confirmCreateDevice()" [disabled]="!createDeviceName().trim() || createDeviceSubmitting()"><span class="p-button-label">Create</span>
                <span class="pi pi-spin pi-spinner mr-2" *ngIf="createDeviceSubmitting()"></span>
            </button>
        } @else {
            <button pButton (click)="showCreateDeviceDialog.set(false)"><span class="p-button-label">Close</span></button>
        }
    </ng-template>
</p-dialog>
